import React, { useEffect, useRef } from "react";
import { Box, Grid, Typography } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { amber, blue, grey } from "@mui/material/colors";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { DND_ITEM_TYPES } from "../supplyTableRow";
import IntersectionObservedView from "../../../../../components/IntersectionObservedView";

const ROW_HEIGHT = 42;

const getRowBgColor = (added, isHistory) => {
  if (added) return grey[200];
  if (isHistory) return amber[50];
  return "transparent";
};

const getRowHoverBgColor = (added, isHistory) => {
  if (added) return grey[300];
  if (isHistory) return amber[100];
  return blue[50];
};

const CombinedSidebarRow = ({
  data,
  part,
  isHistory,
  onSelect,
  onDragBegin,
  onDragCancel,
  onRestore,
}) => {
  const rowRef = useRef(null);
  const isAdded = !!data.added;

  // 履歴は参照データとして使い、新規 supplyDetail として追加する。
  // 履歴由来の id (supplierProductHistoryId / supplyDetailId / supplyId)
  // および isHidden / isHistory / added フラグは持ち込まない。
  const [{ isDragging }, drag, dragPreview] = useDrag(
    () => ({
      type: DND_ITEM_TYPES.ADD_PRODUCT_ROW,
      item: () => {
        onDragBegin?.();
        const {
          supplierProductHistoryId,
          supplyDetailId,
          supplyId,
          isHidden,
          isHistory: _ih,
          added: _added,
          _groupKey,
          ...rest
        } = data;
        return { type: DND_ITEM_TYPES.ADD_PRODUCT_ROW, ...rest, part };
      },
      end: () => {
        onDragCancel?.();
      },
      canDrag: () => !isAdded,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [data, part, isAdded, onDragBegin, onDragCancel]
  );

  drag(rowRef);

  // ネイティブのドラッグ画像を抑制し、<FreeProductDragPreview /> でカスタム描画する
  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, [dragPreview]);

  const origin = data.origin || data.originName || "";
  const displaySize = data.displaySize || [
    data.rankName,
    data.productClassName,
    data.packingName,
  ].filter(Boolean).join(" ");
  const orderingUnit = data.orderingUnit || data.supplyUnitName || "";
  const remark = data.remark || data.remarks || "";

  return (
    <div
      ref={rowRef}
      style={{
        height: ROW_HEIGHT,
        borderBottom: "1px solid rgba(0,0,0,0.12)",
        opacity: isDragging ? 0.4 : 1,
        cursor: isAdded ? "pointer" : "grab",
      }}
    >
      <IntersectionObservedView rootMargin="50px">
        <Box
          onClick={() => onSelect?.(data)}
          sx={{
            height: ROW_HEIGHT,
            bgcolor: getRowBgColor(isAdded, isHistory),
            borderLeft: isHistory && !isAdded ? "3px solid" : "3px solid transparent",
            borderLeftColor: isHistory && !isAdded ? "warning.main" : "transparent",
            transition: "background-color 0.15s ease",
            "&:hover": {
              bgcolor: getRowHoverBgColor(isAdded, isHistory),
            },
          }}
        >
          <Grid
            container
            alignItems="center"
            spacing={0.5}
            sx={{ height: "100%", px: 0.5 }}
          >
            {/* Drag icon */}
            <Grid item xs={0.6} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <DragIndicatorIcon sx={{ fontSize: 16, color: isAdded ? "grey.400" : "primary.main", opacity: isAdded ? 0.3 : 1 }} />
            </Grid>

            {/* 商品名 */}
            <Grid item xs={2.5}>
              <Typography variant="body2" noWrap title={data.productName} sx={{ fontSize: 15 }}>
                {data.productName}
              </Typography>
            </Grid>

            {/* 産地 */}
            <Grid item xs={1.5}>
              <Typography variant="body2" noWrap title={origin} sx={{ fontSize: 15 }}>
                {origin}
              </Typography>
            </Grid>

            {/* 規格 */}
            <Grid item xs={2}>
              <Typography variant="body2" noWrap title={displaySize} sx={{ fontSize: 15 }}>
                {displaySize}
              </Typography>
            </Grid>

            {/* 発注単位 */}
            <Grid item xs={2}>
              <Typography variant="body2" noWrap sx={{ fontSize: 15 }}>
                {orderingUnit}
              </Typography>
            </Grid>

            {/* 備考 */}
            <Grid item xs={3.4}>
              <Typography variant="body2" noWrap title={remark} sx={{ fontSize: 15, color: remark ? "text.primary" : "text.disabled" }}>
                {remark}
              </Typography>
            </Grid>

          </Grid>
        </Box>
      </IntersectionObservedView>
    </div>
  );
};

export default CombinedSidebarRow;
